import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Space, Typography, Progress, Tag, Menu, Dropdown, Grid } from 'antd';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { UserOutlined, LineChartOutlined, BarChartOutlined, PieChartOutlined, RadarChartOutlined } from '@ant-design/icons';
import usePortfolioActions from "./usePortfolioActions";
import Chip from "../../components/Chip";
// Define colors for different groups
const groupColors = {
  size: 'var(--blue)', // Blue for Size Breakdown
  style: 'var(--primary)', // Green for Style Breakdown
};

// Define a list of tags with labels and their corresponding keys
const tagsConfig = [
  { label: 'Asset Class', key: 'asset_class' },
  { label: 'Category', key: 'category' },
  { label: 'Country', key: 'country' },
  { label: 'Industry', key: 'industry' },
  { label: 'Sector', key: 'sector' },
  { label: 'Size', key: 'size' },
  { label: 'Style', key: 'style' },
];

const { useBreakpoint } = Grid;

const ActionMenu = ({ portfolio, handlePortfolioAction, actions, prefix }) => {
  const navigate = useNavigate();
  const url = prefix ? `${prefix}/portfolio/${portfolio.id}` : `/portfolio/${portfolio.id}`;
  return (
    <Menu
      style={{ background: 'rgba(255, 106, 106, 0.3)', backdropFilter: 'blur(10px)' }}
      onClick={({ key }) => {
        handlePortfolioAction(key);
      }}
    >
      <Menu.Item onClick={() => navigate(url)}>View Portfolio</Menu.Item>
      {actions &&
        actions.map((actionItem, index) => (
          <Menu.Item key={actionItem.action} style={{ color: "#fff", fontWeight: '300' }}>
            {actionItem.label}
          </Menu.Item>
        ))}
    </Menu>
  );
};

const PortfolioViewer = ({ portfolios, extraActions, prefix }) => {
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const navigate = useNavigate();
  const { handlePortfolioAction } = usePortfolioActions({ portfolio: selectedPortfolio });
  const screens = useBreakpoint();

  const renderArrayData = (title, array, color) => {
    if (!array || array.length === 0) return null;

    return (
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={5}>{title}</Typography.Title>
        <Row gutter={[16, 16]} className="mt-3">
          {array
            .sort((a, b) => b.v - a.v)
            .slice(0, 3)
            .map((item, index) => (
              <Col key={index} xs={24} sm={12} md={8}>
                <Typography.Text>{item.k.toUpperCase()}</Typography.Text>
                <Progress
                  percent={Math.round(item.v * 100)}
                  showInfo={false}
                  strokeColor={color} // Use the same color for all items in the group
                />
                <Typography.Text>{(item.v * 100).toFixed(2)}%</Typography.Text>
              </Col>
            ))}
        </Row>
      </div>
    );
  };

  const renderTags = (tagsData) => {
    if (!tagsData) return null;

    return (
      <div style={{ marginBottom: 20 }}>
        <Space wrap>
          {tagsConfig.map(({ label, key }) => {
            const value = tagsData[key];
            if (value) {
              return <Chip  label={label} value={value}/>
            }
            return null;
          })}
        </Space>
      </div>
    );
  };

  return (
    <div className="portfolio-viewer">
      {screens.xl ? (
        // Desktop view
        <Row gutter={16}>
          <Col span={6}>
            <div className="dropped-shadow round-2">
              <Row justify="space-between" className="p-3">
                <Typography.Title level={4}>Models</Typography.Title>
              </Row>
              <Space
                direction="vertical"
                style={{ maxHeight: 500, overflowY: 'auto', width: '100%' }}
                className="hide-scrollbar cursor"
              >
                {portfolios.map((portfolio, index) => (
                  <Row
                    key={index}
                    onClick={() => setSelectedPortfolio(portfolio)}
                    className="portfolio-card dropped-shadow"
                    justify="space-between"
                  >
                    <div>
                      <Typography.Title level={5}>{portfolio.name}</Typography.Title>
                      <Typography.Text>{portfolio.description}</Typography.Text>
                    </div>
                    <Typography.Title level={5} style={{ color: '#000', fontWeight: 400 }}>
                      {portfolio?.targets?.equity} / {portfolio?.targets?.fixed}
                    </Typography.Title>
                  </Row>
                ))}
              </Space>
            </div>
          </Col>

          <Col span={16} offset={1}>
            {selectedPortfolio ? (
              <div className="dropped-shadow round-2" style={{ height: '100%' }}>
                <Row justify="space-between" className="p-3">
                  <Typography.Title level={4}>{selectedPortfolio.name}</Typography.Title>
                  <Space>
                    {extraActions && extraActions.length > 0 ? (
                      <Dropdown
                        overlay={
                          <ActionMenu
                            portfolio={selectedPortfolio}
                            actions={extraActions}
                            handlePortfolioAction={handlePortfolioAction}
                            prefix={prefix}
                          />
                        }
                        trigger={['hover']}
                        placement="bottomCenter" // Popover placement
                      >
                        <Button
                          variant="contained"
                          style={{ backgroundColor: '#0057FF', color: '#fff' }}
                        >
                          Try This Strategy
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/portfolio/${selectedPortfolio.id}`)}
                        style={{ backgroundColor: '#0057FF', color: '#fff' }}
                      >
                        Try This Strategy
                      </Button>
                    )}
                  </Space>
                </Row>
                <div style={{ padding: '20px' }}>
                  <Typography.Paragraph ellipsis={{ rows: 10 }}>
                    {selectedPortfolio?.attr?.summary}
                  </Typography.Paragraph>

                  {/* Render tags if they exist */}
                  {renderTags(selectedPortfolio?.tags)}

                  {/* Render size and style if they exist */}
                  {renderArrayData('Allocations by Size', selectedPortfolio?.composite?.size, groupColors.size)}
                  {renderArrayData('Allocations by Style', selectedPortfolio?.composite?.style, groupColors.style)}
                </div>
              </div>
            ) : (
              <Card
                style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Select a portfolio to view details
              </Card>
            )}
          </Col>
        </Row>
      ) : (
        // Mobile view
        <div>
          <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
            <Row justify="space-between" className="p-3">
              <Typography.Title level={4}>Models</Typography.Title>
            </Row>
            <Space direction="horizontal" wrap className="hide-scrollbar cursor">
              {portfolios.map((portfolio, index) => (
                <Card
                  key={index}
                  onClick={() => setSelectedPortfolio(portfolio)}
                  className="portfolio-card bordered cursor gapless p-2"
                >
                  <Typography.Title level={5}>{portfolio.name}</Typography.Title>
                </Card>
              ))}
            </Space>
          </Space>

          {selectedPortfolio ? (
            <div className="dropped-shadow round-2" style={{ height: '100%' }}>
              <Row justify="space-between" className="p-3">
                <Typography.Title level={4}>{selectedPortfolio.name}</Typography.Title>
                <Space>
                  {extraActions && extraActions.length > 0 ? (
                    <Dropdown
                      overlay={
                        <ActionMenu
                          portfolio={selectedPortfolio}
                          actions={extraActions}
                          handlePortfolioAction={handlePortfolioAction}
                        />
                      }
                      trigger={['hover']}
                      placement="bottomCenter"
                    >
                      <Button
                        variant="contained"
                        style={{ backgroundColor: '#0057FF', color: '#fff' }}
                      >
                        Try This Strategy
                      </Button>
                    </Dropdown>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/portfolio/${selectedPortfolio.id}`)}
                      style={{ backgroundColor: '#0057FF', color: '#fff' }}
                    >
                      Try This Strategy
                    </Button>
                  )}
                </Space>
              </Row>
              <div style={{ padding: '20px' }}>
                <Typography.Paragraph ellipsis={{ rows: 10 }}>
                  {selectedPortfolio?.attr?.summary}
                </Typography.Paragraph>
                {renderTags(selectedPortfolio?.tags)}
                {renderArrayData('Allocations by Size', selectedPortfolio?.composite?.size, groupColors.size)}
                {renderArrayData('Allocations by Style', selectedPortfolio?.composite?.style, groupColors.style)}
              </div>
            </div>
          ) : (
            <Card
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              Select a portfolio to view details
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

PortfolioViewer.propTypes = {
  portfolios: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      tags: PropTypes.shape({
        asset_class: PropTypes.string,
        category: PropTypes.string,
        country: PropTypes.string,
        industry: PropTypes.string,
        sector: PropTypes.string,
        size: PropTypes.string,
        style: PropTypes.string,
      }),
      composite: PropTypes.shape({
        size: PropTypes.arrayOf(
          PropTypes.shape({
            k: PropTypes.string.isRequired,
            v: PropTypes.number.isRequired,
          })
        ),
        style: PropTypes.arrayOf(
          PropTypes.shape({
            k: PropTypes.string.isRequired,
            v: PropTypes.number.isRequired,
          })
        ),
      }),
      targets: PropTypes.shape({
        equity: PropTypes.number,
        fixed: PropTypes.number,
      }),
      attr: PropTypes.shape({
        summary: PropTypes.string,
      }),
    })
  ).isRequired,
};

export default PortfolioViewer;